import { useState, useCallback } from 'react';

const STORAGE_KEY = 'funnelBuilder_dismissedTips';

/**
 * Hook for managing dismissed tips in localStorage
 * Allows tips to be permanently or temporarily dismissed
 */
export const useDismissedTips = () => {
    const [dismissedTips, setDismissedTips] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    // Temporarily dismissed tips (session only)
    const [sessionDismissed, setSessionDismissed] = useState([]);

    const dismissTip = useCallback((tipId, permanent = false) => {
        if (permanent) {
            setDismissedTips(prev => {
                const updated = [...new Set([...prev, tipId])];
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
                } catch (e) {
                    console.error('Failed to save dismissed tips:', e);
                }
                return updated;
            });
        } else {
            setSessionDismissed(prev => [...new Set([...prev, tipId])]);
        }
    }, []);

    const isTipDismissed = useCallback((tipId) => {
        return dismissedTips.includes(tipId) || sessionDismissed.includes(tipId);
    }, [dismissedTips, sessionDismissed]);

    const resetTips = useCallback(() => {
        setDismissedTips([]);
        setSessionDismissed([]);
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            console.error('Failed to reset dismissed tips:', e);
        }
    }, []);

    const resetSessionTips = useCallback(() => {
        setSessionDismissed([]);
    }, []);

    return {
        dismissedTips: [...dismissedTips, ...sessionDismissed],
        dismissTip,
        isTipDismissed,
        resetTips,
        resetSessionTips
    };
};

export default useDismissedTips;
